import { insertContainerHandle } from './insertContainerHandle';
import { handleIframeFailure } from './handleIframeFailure';
import {
  CONTAINER_COLLAPSE_ANIMATION_TIMEOUT_S,
  CONTAINER_COLLAPSED_CLASS,
  CONTAINER_ID,
  DROP_ZONE_ID,
  FRAME_ID,
  HANDLE_ID,
  LOGO_ID,
  LOGO_MOVING_CLASS,
  SESSION_COLLAPSED,
  SESSION_CONTAINER_HEIGHT,
  CONTAINER_EXPANDING_CLASS,
} from './constants';
import { configureLogoBubble, getLogoPosition, getAppearAnimation, LogoPosition } from './configureLogoBubble';
import { insertCloseButton } from './insertCloseButton';
import {
  insertABtastyEventNotification,
  QA_ASSISTANT_NOTIFICATION_EVENT,
  createABTastyNotificationListener,
} from './insertNotification';
import { applyContainerCollapseStyles, applyContainerExpandStyles } from './actions/collapseQAAssistant';
import { ABTASTY_QA_PLATFORM_URL, SEARCH_PARAM_OAUTH_CODE_V1 } from '@src/shared/constants';
import { PlatformData } from './type';
import { appSrcByEnv } from './helpers';

const createSearch = ({
  apiOauthCodeV1,
  fsEnvId,
  platformData,
}: {
  apiOauthCodeV1?: string | null;
  fsEnvId?: string;
  platformData: PlatformData;
}) => {
  const search = new URLSearchParams();
  if (apiOauthCodeV1) search.append(SEARCH_PARAM_OAUTH_CODE_V1, apiOauthCodeV1);
  const searchString = search.toString();
  const searchCreated = searchString ? `&${searchString}` : '';
  const fsEnvIdQuery = fsEnvId ? `&fsEnvId=${fsEnvId}` : '';
  const fsUrl = platformData.fsUrl ? `&fsUrl=${platformData.fsUrl}` : '';
  const tagUrl = platformData.tagUrl ? `&tagUrl=${platformData.tagUrl}` : '';
  return `?platformType=${platformData.type}${fsUrl}${tagUrl}${searchCreated}${fsEnvIdQuery}`;
};

export type InsertContainerArg = {
  appUrlSrc: string;
  platformData: PlatformData;
  onIframeLoaded: (iframe: HTMLIFrameElement) => void;
  apiOauthCodeV1?: string | null;
  longPressTimeoutMs?: number;
};

const getMainCss = (collapseAnimationTime: string, logoPosition: LogoPosition) => `
    .abtasty-highlight-event {
      position: relative;
    }
    .abtasty-highlight-event:after {
      top: 0 !important;
      left: 0 !important;
      position: absolute !important;
      content: '' !important;
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      box-shadow: inset 0 0 0 10px #d54040, 5px 5px 13px 5px rgba(0,0,0,0.4) !important;
      background: rgba(213,64,64,0.3) !important;
    }
    #${CONTAINER_ID}{
      background: transparent !important;
    }
    #${CONTAINER_ID}.${CONTAINER_EXPANDING_CLASS} {
      animation-name: collapse;
      animation-duration: ${collapseAnimationTime};
      animation-timing-function: ease-out;
      animation-fill-mode: forwards;
      animation-direction: reverse;
    }
    #${CONTAINER_ID}.${CONTAINER_COLLAPSED_CLASS} {
      min-width: unset !important;
      animation-name: collapse;
      animation-duration: ${collapseAnimationTime};
      animation-delay: ${collapseAnimationTime};
      animation-timing-function: ease-out;
      animation-direction: normal;
      animation-fill-mode: forwards;
    }

    #${CONTAINER_ID}.${CONTAINER_COLLAPSED_CLASS} iframe {
      animation-name: spotlight;
      animation-duration: ${collapseAnimationTime};
      animation-timing-function: ease-out;
      animation-direction: normal;
      animation-fill-mode: forwards;
    }
    #${CONTAINER_ID}.${CONTAINER_EXPANDING_CLASS} iframe {
      animation-name: spotlight;
      animation-duration: ${collapseAnimationTime};
      animation-delay: ${collapseAnimationTime};
      animation-timing-function: ease-out;
      animation-fill-mode: both;
      animation-direction: reverse;
    }

    #${CONTAINER_ID}.${CONTAINER_COLLAPSED_CLASS} #${HANDLE_ID} {
      display:none
    }
    #${DROP_ZONE_ID} {
      visibility: hidden;
      opacity: 0;
      transition: visibility 0s, opacity 0.5s linear;
    }
    #${DROP_ZONE_ID}.${LOGO_MOVING_CLASS} {
      visibility: visible;
      opacity: 1;
    }

    #${CONTAINER_ID} #${LOGO_ID} {
      height: 30px;
      width: 30px;
      padding: 0;
      opacity: 0;
      pointer-events: none;
      box-sizing: content-box !important;
      pointer-events: all;
      z-index: 100;
      position: fixed;
      top: unset;
      left: ${logoPosition.left};
      bottom: ${logoPosition.bottom};
      right: unset;
      display: none;
      user-select: none;
      -moz-user-select: none;
      -webkit-user-select: none;
    }
    #${CONTAINER_ID} #${LOGO_ID}.ab-assistant-not-loaded{
      opacity:0 !important;
    }
    #${CONTAINER_ID} #${LOGO_ID}.${LOGO_MOVING_CLASS} {
      cursor: grabbing !important;
      transition: transform 1s ease !important;
      transform: scale(0.8) !important;
    }
    #${CONTAINER_ID}.${CONTAINER_COLLAPSED_CLASS} #${LOGO_ID} {
      display:block;
      animation-name: appear;
      animation-duration: 1s;
      animation-timing-function: cubic-bezier(0.72, 0.18, 0, 1.2);
      animation-direction: normal;
      animation-fill-mode: forwards;
    }
    #${CONTAINER_ID}.${CONTAINER_EXPANDING_CLASS} #${LOGO_ID} {
      display:block;
      animation-name: appear;
      animation-duration: 1s;
      animation-timing-function: cubic-bezier(0.72, 0.18, 0, 1.2);
      animation-direction: reverse;
      animation-fill-mode: forwards;
    }

    .${QA_ASSISTANT_NOTIFICATION_EVENT} {
      display: none;
      font-size: 10px;
      font-weight: 900;
      width: 16px;
      height: 16px;
      background: #d54040;
      color: white;
      border-radius: 16px;
      cursor: pointer;
      position: absolute;
      top: -2px;
      right: -13px;
      font-family: Helvetica, Arial, sans-serif;
    }
    .${QA_ASSISTANT_NOTIFICATION_EVENT}.active {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qa-assistant-close{
      display:none;
    }

    @media (hover: hover) {
      #${LOGO_ID}:not(.${LOGO_MOVING_CLASS}):hover .qa-assistant-close,.qa-assistant-close:hover {
        display: block;
        cursor: pointer;
        position: absolute;
        top: -5px;
        right: -15px;
      }
      #${LOGO_ID}:not(.${LOGO_MOVING_CLASS}):hover .${QA_ASSISTANT_NOTIFICATION_EVENT}.active, .${QA_ASSISTANT_NOTIFICATION_EVENT}.active:hover {
        display: none;
      }
    }

    @keyframes spotlight {
      0% { clip-path: circle(max(150%, 150%) at 79px 26px); box-shadow: 0px; pointer-events:all; }
      100% { clip-path: circle(0px at 85px 26px); box-shadow: 0px; pointer-events:none;}
    }
    @keyframes collapse {
      0% {left:0; bottom:0;width:100%;max-height:calc(100% - 100px);box-shadow:rgba(0, 0, 0, 0.16) 0px 5px 40px;}
      1% {left:0; bottom:0;width:100%;max-height:calc(100% - 100px);box-shadow:rgba(0, 0, 0, 0) 0px 5px 40px}
      100%{left:20px;bottom:20px;width:56px;max-height:56px;box-shadow: rgba(0, 0, 0, 0) 0px 5px 40px;}
    }
  }`;

export const insertContainer = ({ appUrlSrc, onIframeLoaded, apiOauthCodeV1, platformData }: InsertContainerArg) => {
  const { cookieEnabled } = navigator;

  let iframeSrc: string;
  const platformUrl = sessionStorage.getItem(ABTASTY_QA_PLATFORM_URL);
  const isPlatformUrlValid = platformUrl && Object.values(appSrcByEnv).some((envUrl) => platformUrl.startsWith(envUrl));
  if (isPlatformUrlValid) {
    iframeSrc = platformUrl;
  } else if (platformUrl && !isPlatformUrlValid) {
    console.error('[ABTasty][QA-Assistant] The platform URL is not valid');
    return;
  } else {
    const envId = window.ABTastyWebSdk?.envId || window.flagship?.envId;
    const search = createSearch({ apiOauthCodeV1, fsEnvId: envId, platformData });
    iframeSrc = `${appUrlSrc}/index.html${search}`;
  }
  // Container
  const container = document.createElement('div');
  container.id = CONTAINER_ID;

  const containerHeight = cookieEnabled ? sessionStorage.getItem(SESSION_CONTAINER_HEIGHT) || '500px' : '500px';
  const containerStyle = `
    border: none;
    width: 100%;
    z-index: 2147483647;
    position: fixed;
    bottom: 0px;
    right: 0px;
    left: 0;
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.25);
    max-height: calc(100% - 100px);
    min-height: 56px;
    height: ${containerHeight};
    background: #f7f7f7;
    border-radius: 24px 24px 0 0;
    min-width:100%;
  `;
  container.setAttribute('style', containerStyle);

  container.insertAdjacentHTML(
    'beforeend',
    `<div id="${DROP_ZONE_ID}" style="
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      background-color: rgba(225, 225, 225, 0.8);
      width: 44px;
      height: 44px;
      align-items: center;
      justify-content: center;
      border-radius: 22px;
      z-index: 101;
    ">
      <svg data-name="cricles bg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" style="min-width: 24px; min-height: 24px;"><path style="fill: #393939;" d="M18 7.49L16.51 6 12 10.51 7.49 6 6 7.49 10.51 12 6 16.51 7.49 18 12 13.49 16.51 18 18 16.51 13.49 12 18 7.49z"></path></svg>
    </div>`
  );

  container.insertAdjacentHTML(
    'beforeend',
    `<div id="${LOGO_ID}" class="ab-assistant-not-loaded" style="
      position: fixed;
      transition: 200ms ease;
      transition-property: left, right;
      cursor: pointer;
      padding: 4px;
      height: 60px;
      width: 60px;
      opacity: 1;
    ">
      <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="30" fill="#C5B9E9"/><circle cx="30" cy="30" r="24" fill="#3100BF"/><path d="M38.2224 34.9616L41.5385 20H28.8763L27.4024 26.7179H23.3741L21.9249 33.1667H19.8002L18.4615 39.2308H22.1091L22.2933 38.359L20.5003 37.5H27.7341L27.9797 36.3847L24.7865 34.9616H38.2224Z" fill="#C0E605"/></svg>
    </div>`
  );

  const collapseAnimationTime = `${CONTAINER_COLLAPSE_ANIMATION_TIMEOUT_S}s`;
  const logoPosition = getLogoPosition();
  const css = getMainCss(collapseAnimationTime, logoPosition);

  // CSS animations
  const customStyles = document.createElement('style');
  customStyles.innerHTML = css;
  container.append(customStyles);

  const animationAppearStyle = document.createElement('style');
  animationAppearStyle.setAttribute('id', 'animationAppearStyle');
  animationAppearStyle.innerHTML = getAppearAnimation(getLogoPosition());
  container.append(animationAppearStyle);

  // minimized saved
  if (cookieEnabled && sessionStorage.getItem(SESSION_COLLAPSED)) {
    applyContainerCollapseStyles(container);
  } else {
    applyContainerExpandStyles(container);
  }

  // Frame
  const iframe = document.createElement('iframe');
  iframe.id = FRAME_ID;
  iframe.src = iframeSrc;
  iframe.title = 'AB Tasty QA Assistant';
  const iframeStyle = `
    border: none;
    width: 100%;
    height: 100%;
  `;
  iframe.setAttribute('style', iframeStyle);
  iframe.setAttribute('name', 'ABTastyQaAssistant');
  iframe.onload = () => onIframeLoaded(iframe);
  iframe.allow = 'clipboard-write';
  container.appendChild(iframe);

  document.body.appendChild(container);

  configureLogoBubble(container);
  // closing Button and Notification badge
  const logo = document.getElementById(LOGO_ID);
  const abtastyNotificationListener = createABTastyNotificationListener();

  insertABtastyEventNotification(logo!);
  insertCloseButton(logo!, abtastyNotificationListener);

  insertContainerHandle(container.id, iframe.id);

  const iframeError = (e) => {
    if (e.blockedURI.includes('qa-assistant.abtasty.com') && e.disposition !== 'report') {
      handleIframeFailure(container);
      iframe.remove();
      document.removeEventListener('securitypolicyviolation', iframeError);
    }
  };

  document.addEventListener('securitypolicyviolation', iframeError);

  return iframe;
};
